import { gql, useMutation } from "@apollo/client";
import { ProcessRow_ProcessFragment, ProcessRow_RetryProcessMutation, ProcessRow_RetryProcessMutationVariables, ProcessRow_RunProcessMutation, ProcessRow_RunProcessMutationVariables } from "../../../codegen/graphql";
import { MdPlayArrow } from "react-icons/md";
import { formatDate, getDuration, getStatusBadge, getStatusIcon } from "./processesUtils";
import Button from "../common/Button";
import { MdRefresh, MdVisibility } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import { useModalRouter } from "../../ModalRouter_context";

export const PROCESS_ROW_FRAGMENT = gql`
  fragment ProcessRow_Process on Process {
    id
    name
    description
    status
    arguments
    functionName
    logLines
    result
    startedAt
    completedAt
    createdAt
    updatedAt
    rootSubProcessesFlat {
      id
      status
    }
  }
`;

const RUN_PROCESS = gql`
  mutation ProcessRow_RunProcess($id: ID!) {
    runProcess(id: $id) {
      process {
        ...ProcessRow_Process
      }
      result
      error
      logLines
    }
  }
  ${PROCESS_ROW_FRAGMENT}
`;


const RETRY_PROCESS = gql`
  mutation ProcessRow_RetryProcess($id: ID!) {
    retryProcess(id: $id) {
      ...ProcessRow_Process
    }
  }
  ${PROCESS_ROW_FRAGMENT}
`;

type ProcessRow = ProcessRow_ProcessFragment;

interface ProcessRowProps {
  process: ProcessRow;
  level: number;
}

export function ProcessRow({ process, level }: ProcessRowProps) {
  const rootSubProcessesFlat = (process as any).rootSubProcessesFlat ?? [];
  const succeededCount = rootSubProcessesFlat.filter((p: { status: string }) => p.status === 'success').length;
  const erroredCount = rootSubProcessesFlat.filter((p: { status: string }) => p.status === 'error').length;
  const navigate = useNavigate();
  const modalRouter = useModalRouter();

  
  const [retryProcess] = useMutation<
    ProcessRow_RetryProcessMutation,
    ProcessRow_RetryProcessMutationVariables
  >(RETRY_PROCESS, {
    onCompleted: () => {
      // refetch();
    },
  });

  const handleRetry = (id: string) => {
    if (window.confirm('Are you sure you want to retry this process?')) {
      retryProcess({ variables: { id } });
    }
  };

  const openProcessModal = (process: ProcessRow) => {
    modalRouter.push({ s: 'processModal', a: { processId: process.id } });
  };

  return (
    <div
      className={`grid cursor-pointer grid-cols-12 hover:bg-gray-50 transition-colors border-b border-gray-200`}
      style={{ paddingLeft: `${level * 24}px` }}
      onClick={() => openProcessModal(process)}
    >
      <div className="col-span-6 px-6 py-4 flex items-center">
        <div className="w-5 mr-2" />
        {getStatusIcon(process.status)}
        <div className="ml-3">
          <div className="font-medium text-gray-900">{process.name}</div>
          {process.description && (
            <div className="text-gray-500">{process.description}</div>
          )}
          {process.functionName && (
            <div className="text-xs text-gray-400 font-mono">{process.functionName}</div>
          )}
        </div>
      </div>
      <div className="col-span-2 px-6 py-4 flex items-start flex-col">
        {getDuration(process.startedAt, process.completedAt)}<br/>
        {getStatusBadge(process.status)}
        {(process.status === 'error' || process.status === 'partial') && (
          <Button
            onClick={(event) => {
              event.stopPropagation();
              handleRetry(process.id);
            }}
            preset="primary"
            size="sm"
          >
            <MdRefresh size={18} /> Retry
          </Button>
        )}
      </div>
      <div className="col-span-2 px-6 py-4 flex items-center">
        <div className="text-gray-900 text-sm">
          {rootSubProcessesFlat.length > 0 ? (
            <span>{succeededCount} succeeded, {erroredCount} errored</span>
          ) : (
            <span>—</span>
          )}
        </div>
      </div>
      <div className="col-span-2 px-6 py-4 flex items-center">
        <div className="text-gray-900">
          {formatDate(process.createdAt)}
        </div>
      </div>
    </div>
  );
}